import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { testimonials as defaultTestimonials } from "../mock";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState(defaultTestimonials);
  useEffect(() => {
    fetch("/api/content/testimonials")
      .then((r) => r.ok ? r.json() : null)
      .then((json) => { if (json?.data?.length) setTestimonials(json.data); })
      .catch(() => {});
  }, []);

  const [i, setI] = useState(0);
  const [direction, setDirection] = useState(1);
  const t = testimonials[i] || testimonials[0];
  const next = () => { setDirection(1); setI((i + 1) % testimonials.length); };
  const prev = () => { setDirection(-1); setI((i - 1 + testimonials.length) % testimonials.length); };

  return (
    <section id="testimonials" className="relative py-14 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#73100D]/25 bg-[#73100D]/5 text-[#73100D] text-[11px] font-semibold tracking-[0.18em] uppercase mb-5">
              Client Voices
            </div>
            <h2 className="text-3xl md:text-5xl font-bold text-[#111] leading-tight">
              Trusted by{" "}
              <span className="brand-gradient-text">enterprise leaders</span>
            </h2>
            <p className="mt-4 text-[#555] max-w-md">
              Engineering teams worldwide rely on RSP to ship AI products, automate operations and accelerate growth.
            </p>

            <div className="mt-8 flex items-center gap-3">
              <button onClick={prev} className="w-11 h-11 rounded-full border border-[#73100D]/25 hover:border-[#73100D] hover:bg-[#73100D]/5 text-[#73100D] grid place-items-center transition" aria-label="Previous">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={next} className="w-11 h-11 rounded-full btn-primary grid place-items-center" aria-label="Next">
                <span><ChevronRight className="w-5 h-5" /></span>
              </button>
              <div className="ml-3 flex items-center gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => { setDirection(idx > i ? 1 : -1); setI(idx); }}
                    className={`h-1.5 rounded-full transition-all duration-500 ${
                      idx === i ? "w-8 bg-[#73100D]" : "w-1.5 bg-[#73100D]/25 hover:bg-[#73100D]/50"
                    }`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 relative h-[360px] md:h-[320px]"
          >
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={t.id}
                custom={direction}
                initial={{ opacity: 0, x: direction * 40, scale: 0.97 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, x: -direction * 40, scale: 0.97 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 rounded-3xl border border-[#E5E5E5] bg-white p-8 md:p-10 soft-shadow"
              >
                <Quote className="w-12 h-12 text-[#73100D]/15 absolute top-6 right-6" />
                <div className="flex gap-1 text-[#73100D]">
                  {Array.from({ length: 5 }).map((_, k) => (
                    <motion.div
                      key={k}
                      initial={{ opacity: 0, scale: 0, rotate: -45 }}
                      animate={{ opacity: 1, scale: 1, rotate: 0 }}
                      transition={{ delay: 0.2 + k * 0.08, type: "spring", stiffness: 260, damping: 20 }}
                    >
                      <Star className="w-4 h-4 fill-[#73100D]" />
                    </motion.div>
                  ))}
                </div>
                <p className="mt-5 text-lg md:text-xl text-[#333] leading-relaxed font-serif italic">
                  &ldquo;{t.text}&rdquo;
                </p>
                <div className="mt-8 flex items-center gap-4 pt-6 border-t border-[#E5E5E5]">
                  <motion.img
                    src={t.avatar} alt={t.name}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    className="w-12 h-12 rounded-full border-2 border-[#73100D]/30"
                  />
                  <div>
                    <div className="text-[#111] font-semibold">{t.name}</div>
                    <div className="text-sm text-[#666]">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
